import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/authService';
import { Pagination as PaginationType } from '../types';
import { colors } from '../styles/colors';
import Pagination from './Pagination';
import RecruiterCompaniesModal from './RecruiterCompaniesModal';
import RecruiterOpportunitiesModal from './RecruiterOpportunitiesModal';
import RecruiterDetailModal from './RecruiterDetailModal';
import RecruiterEditModal from './RecruiterEditModal';
import RecruiterCreateModal from './RecruiterCreateModal';
import DeleteRecruiterModal from './DeleteRecruiterModal';

interface Company {
  id: number;
  name: string;
}

interface Recruiter {
  id: number;
  email: string;
  name?: string;
  companies: Company[];
  company_count: number;
  opportunities_count: number;
  confirmed_at?: string;
  created_at: string;
}

const Recruiters: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recruiters, setRecruiters] = useState<Recruiter[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('email');
  const [confirmedFilter, setConfirmedFilter] = useState('all');
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [expandedCompanies, setExpandedCompanies] = useState<Set<number>>(new Set());
  const [showCompaniesModal, setShowCompaniesModal] = useState(false);
  const [showOpportunitiesModal, setShowOpportunitiesModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedRecruiter, setSelectedRecruiter] = useState<Recruiter | null>(null);


  useEffect(() => {
    const recruiterId = searchParams.get('recruiterId');
    const action = searchParams.get('action');
    if (!recruiterId) return;

    setSearchParams({});
    setSearchType('id');
    setSearch(recruiterId);
    setSearchTerm(recruiterId);
    setPagination(prev => ({ ...prev, page: 1 }));

    const fetchDirect = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: '1',
          limit: pagination.limit.toString(),
          search: recruiterId,
          searchType: 'id',
        });
        const response = await api.get(`/recruiter-list?${params}`);
        const fetchedRecruiters = response.data.recruiters || [];
        setRecruiters(fetchedRecruiters);
        setPagination(response.data.pagination);

        if (fetchedRecruiters[0]) {
          setSelectedRecruiter(fetchedRecruiters[0]);
          if (action === 'edit') {
            setShowEditModal(true);
          } else if (action === 'view') {
            setShowDetailModal(true);
          }
        }
      } catch (error: any) {
        if (error.name !== 'CanceledError') {
          toast.error('Erro ao carregar recrutador');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDirect();
  }, [location.search]);

  useEffect(() => {
    fetchRecruiters();
  }, [pagination.page, searchTerm, confirmedFilter]);

  const fetchRecruiters = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(confirmedFilter !== 'all' && { confirmed: confirmedFilter === 'confirmed' ? '1' : '0' }),
        ...(searchTerm && { search: searchTerm, searchType }),
      });

      const response = await api.get(`/recruiter-list?${params}`);
      setRecruiters(response.data.recruiters);
      setPagination(response.data.pagination);
      
    } catch (error: any) {
      if (error.name !== 'CanceledError') {
        toast.error('Erro ao carregar recrutadores');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchTypeChange = (newType: string) => {
    setSearchType(newType);
    setSearch('');
    setSearchTerm('');
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleConfirmedFilterChange = (newFilter: string) => {
    setConfirmedFilter(newFilter);
    setSearch('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleActionMenu = (recruiterId: number) => {
    setOpenActionMenu(openActionMenu === recruiterId ? null : recruiterId);
  };

  const toggleCompaniesExpand = (recruiterId: number) => {
    setExpandedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recruiterId)) {
        newSet.delete(recruiterId);
      } else {
        newSet.add(recruiterId);
      }
      return newSet;
    });
  };

  const handleCompanyClick = (companyName: string) => {
    navigate(`/companies?companyName=${encodeURIComponent(companyName)}`);
  };

  const handleCompanyClickById = (companyId: number) => {
    navigate(`/companies?companyId=${companyId}`);
  };

  const handleView = (recruiter: Recruiter) => {
    setSelectedRecruiter(recruiter);
    setShowDetailModal(true);
    setOpenActionMenu(null);
  };

  const handleEdit = (recruiter: Recruiter) => {
    setSelectedRecruiter(recruiter);
    setShowEditModal(true);
    setOpenActionMenu(null);
  };

  const handleDelete = (recruiter: Recruiter) => {
    setSelectedRecruiter(recruiter);
    setShowDeleteModal(true);
    setOpenActionMenu(null);
  };

  const confirmDelete = async (reason: string) => {
    if (!selectedRecruiter) return;
    
    try {
      setDeleting(true);
      await api.delete(`/recruiter-list/${selectedRecruiter.id}`, { data: { reason } });
      toast.success('Recrutador excluído com sucesso!');
      setShowDeleteModal(false);
      setSelectedRecruiter(null);
      fetchRecruiters();
    } catch (error) {
      toast.error('Erro ao excluir recrutador');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewCompanies = (recruiter: Recruiter) => {
    setSelectedRecruiter(recruiter);
    setShowCompaniesModal(true);
    setOpenActionMenu(null);
  };

  const handleViewOpportunities = (recruiter: Recruiter) => {
    setSelectedRecruiter(recruiter);
    setShowOpportunitiesModal(true);
    setOpenActionMenu(null);
  };

  return (
    <div style={styles.container} onClick={() => setOpenActionMenu(null)}>
      <div style={styles.header}>
        <h1 style={styles.title}>Recrutadores</h1>
        <button onClick={() => setShowCreateModal(true)} style={styles.createBtn}>
          Novo Recrutador
        </button>
      </div>

      <div style={styles.scopes}>
        <button 
          onClick={() => handleConfirmedFilterChange('all')} 
          style={{...styles.scopeBtn, ...(confirmedFilter === 'all' ? styles.scopeBtnActive : {})}}
        >
          Todos
        </button>
        <button 
          onClick={() => handleConfirmedFilterChange('confirmed')} 
          style={{...styles.scopeBtn, ...(confirmedFilter === 'confirmed' ? styles.scopeBtnActive : {})}}
        >
          Confirmados
        </button>
        <button 
          onClick={() => handleConfirmedFilterChange('not-confirmed')} 
          style={{...styles.scopeBtn, ...(confirmedFilter === 'not-confirmed' ? styles.scopeBtnActive : {})}}
        >
          Não Confirmados
        </button>
      </div>
      
      <form onSubmit={handleSearch} style={styles.filters}>
        <select
          value={searchType}
          onChange={(e) => handleSearchTypeChange(e.target.value)}
          style={styles.select}
        >
          <option value="email">Email</option>
          <option value="name">Nome</option>
          <option value="company">Empresa</option>
          <option value="id">ID</option>
        </select>
        <input
          type="text"
          placeholder={`Buscar por ${searchType === 'email' ? 'email' : searchType === 'name' ? 'nome' : searchType === 'company' ? 'empresa' : 'ID'}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.input}
        />
        <button type="submit" style={styles.searchBtn}>
          Buscar
        </button>
      </form>

      {loading ? (
        <div style={styles.loading}>Carregando...</div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Nome</th>
                  <th style={styles.th}>E-mail</th>
                  <th style={styles.th}>Empresas</th>
                  <th style={styles.th}>Associação</th>
                  <th style={styles.th}>Confirmed At</th>
                  <th style={styles.th}>Created At</th>
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {recruiters.map((recruiter) => (
                  <tr key={recruiter.id} style={styles.tableRow}>
                    <td style={styles.td}>{recruiter.id}</td>
                    <td style={styles.td}>{recruiter.name || '-'}</td>
                    <td style={styles.td}>{recruiter.email}</td>
                    <td style={styles.td}>
                      {recruiter.companies && recruiter.companies.length > 0 ? (
                        <div style={styles.companiesCell}>
                          <div style={styles.companiesList}>
                            {(expandedCompanies.has(recruiter.id) 
                              ? recruiter.companies 
                              : recruiter.companies.slice(0, 3)
                            ).map((company) => (
                              <div key={company.id}>
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleCompanyClickById(company.id);
                                  }}
                                  style={styles.companyLink}
                                >
                                  {company.name}
                                </a>
                              </div>
                            ))}
                          </div>
                          {recruiter.companies.length > 3 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCompaniesExpand(recruiter.id);
                              }}
                              style={styles.expandBtn}
                            >
                              {expandedCompanies.has(recruiter.id) 
                                ? '− Mostrar menos' 
                                : `+ ${recruiter.companies.length - 3} mais`}
                            </button>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.associationBtns}>
                        <button
                          onClick={() => handleViewCompanies(recruiter)}
                          style={styles.associationBtn}
                        >
                          Empresas ({recruiter.company_count})
                        </button>
                        <button
                          onClick={() => handleViewOpportunities(recruiter)}
                          style={styles.associationBtn}
                        >
                          Oportunidades ({recruiter.opportunities_count || 0})
                        </button>
                      </div>
                    </td>
                    <td style={styles.td}>
                      {recruiter.confirmed_at ? (
                        <>
                          {new Date(recruiter.confirmed_at).toLocaleDateString('pt-BR')}
                          <br />
                          <span style={styles.timeText}>
                            {new Date(recruiter.confirmed_at).toLocaleTimeString('pt-BR')}
                          </span>
                        </>
                      ) : (
                        <span style={styles.notConfirmedBadge}>NÃO CONFIRMADO</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {new Date(recruiter.created_at).toLocaleDateString('pt-BR')}
                      <br />
                      <span style={styles.timeText}>
                        {new Date(recruiter.created_at).toLocaleTimeString('pt-BR')}
                      </span>
                    </td>
                    <td style={styles.tdActions}>
                      <div style={styles.actionMenuContainer}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(recruiter.id);
                          }}
                          style={styles.actionMenuBtn}
                          title="Ações"
                        >
                          ⋮
                        </button>
                        {openActionMenu === recruiter.id && (
                          <div style={styles.actionMenu}>
                            <button
                              onClick={() => handleView(recruiter)}
                              style={styles.actionMenuItem}
                            >
                              Ver Detalhes
                            </button>
                            <button
                              onClick={() => handleEdit(recruiter)}
                              style={styles.actionMenuItem}
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleDelete(recruiter)}
                              style={{...styles.actionMenuItem, color: colors.error}}
                            >
                              Excluir
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.paginationContainer}>
            <div style={styles.paginationInfo}>
              Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
            </div>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
      
      {showCompaniesModal && selectedRecruiter && (
        <RecruiterCompaniesModal
          recruiterId={selectedRecruiter.id}
          recruiterName={selectedRecruiter.name || selectedRecruiter.email}
          onClose={() => {
            setShowCompaniesModal(false);
            setSelectedRecruiter(null);
          }}
        />
      )}
      
      {showOpportunitiesModal && selectedRecruiter && (
        <RecruiterOpportunitiesModal
          recruiterId={selectedRecruiter.id}
          recruiterName={selectedRecruiter.name || selectedRecruiter.email}
          onClose={() => {
            setShowOpportunitiesModal(false);
            setSelectedRecruiter(null);
          }}
        />
      )}
      
      {showDetailModal && selectedRecruiter && (
        <RecruiterDetailModal
          recruiterId={selectedRecruiter.id}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedRecruiter(null);
          }}
        />
      )}
      
      {showEditModal && selectedRecruiter && (
        <RecruiterEditModal
          recruiterId={selectedRecruiter.id}
          onClose={() => {
            setShowEditModal(false);
            setSelectedRecruiter(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setSelectedRecruiter(null);
            fetchRecruiters();
          }}
        />
      )}
      
      {showCreateModal && (
        <RecruiterCreateModal
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            fetchRecruiters();
          }}
        />
      )}
      
      {showDeleteModal && selectedRecruiter && (
        <DeleteRecruiterModal
          isOpen={showDeleteModal}
          recruiterName={selectedRecruiter.name || selectedRecruiter.email}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedRecruiter(null);
          }}
          loading={deleting}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  title: {
    fontSize: '2rem',
    color: colors.primary,
    marginBottom: '2rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
  },
  select: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '120px',
    backgroundColor: colors.white,
  },
  input: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '300px',
  },
  searchBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    overflow: 'visible' as const,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    overflow: 'visible' as const,
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: '600',
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '1rem',
    color: colors.text,
  },
  paginationContainer: {
    marginTop: '2rem',
  },
  paginationInfo: {
    textAlign: 'center' as const,
    color: colors.textLight,
    marginBottom: '1rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  createBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  scopes: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  scopeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  scopeBtnActive: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  tdActions: {
    padding: '1rem',
    color: colors.text,
    minWidth: '80px',
    position: 'relative' as const,
  },
  actionMenuContainer: {
    position: 'relative' as const,
  },
  actionMenuBtn: {
    padding: '8px 12px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  actionMenu: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '120px',
  },
  actionMenuItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  notConfirmedBadge: {
    backgroundColor: colors.error,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
  },
  timeText: {
    fontSize: '0.85rem',
    color: colors.textLight,
  },
  companiesCell: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  companiesList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  companyLink: {
    color: colors.primary,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  expandBtn: {
    padding: '0.25rem 0.5rem',
    backgroundColor: colors.white,
    color: colors.primary,
    border: `1px solid ${colors.primary}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontFamily: 'inherit',
    alignSelf: 'flex-start',
  },
  associationBtns: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  associationBtn: {
    padding: '0.4rem 0.8rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    textAlign: 'left' as const,
    textWrap: 'nowrap' as const,
  },
};

export default Recruiters;
